import { mdast2hastGridTablesHandler } from '@adobe/mdast-util-gridtables';
import remarkGridTable from '@adobe/remark-gridtables';
import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Heading from 'components/atoms/Heading/Heading';
import Table from 'components/atoms/Table/Table';
import { useNamespace } from 'components/templates/Translation/TranslationWrapper';
import { Trans, useTranslation } from 'next-i18next';
import React, {
  AnchorHTMLAttributes,
  ClassAttributes,
  FunctionComponent,
  ReactElement,
} from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSlug from 'rehype-slug';
import remarkSupersub from 'remark-supersub';
import classes from './Txt.module.scss';

type MarkdownComponents = NonNullable<React.ComponentProps<typeof ReactMarkdown>['components']>;

export type PolymorphicRef<C extends React.ElementType> = React.ComponentPropsWithRef<C>['ref'];

interface BaseProps<C extends React.ElementType> {
  component?: C;
  children: string;
  className?: string;
  skipTranslation?: boolean;
  bold?: boolean;
  size?: 'small' | 'normal' | 'large' | 'xlarge' | 'xxlarge';
  color?: 'primary' | 'secondary' | 'default';
  asMarkdown?: boolean;
  markdownComponents?: MarkdownComponents;
  uppercase?: boolean;
}

const MarkdownLink: FunctionComponent<
  ClassAttributes<HTMLAnchorElement> & AnchorHTMLAttributes<HTMLAnchorElement>
> = function ({ href, children }) {
  const additionalProps = href?.toString().startsWith('http')
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};
  return (
    <A href={href?.toString() ?? '/missingpath'} {...additionalProps}>
      {children}
    </A>
  );
};

export type Props<C extends React.ElementType> = BaseProps<C> &
  Omit<React.ComponentPropsWithRef<C>, keyof BaseProps<C>>;

const Txt = <C extends React.ElementType = 'span'>(
  {
    children,
    component,
    skipTranslation,
    translateOptions,
    color,
    size,
    bold,
    asMarkdown,
    markdownComponents,
    uppercase,
    ...rest
  }: Props<C>,
  ref: PolymorphicRef<C>,
): ReactElement => {
  const namespace = useNamespace();
  const { t } = useTranslation(namespace);

  const className = clsx(
    rest.className,
    color && classes[color],
    size && classes[size],
    bold && classes.bold,
    uppercase && classes.uppercase,
    asMarkdown && classes.markdown,
  );
  const Root = component || 'span';

  return (
    <Root {...rest} className={className} ref={ref}>
      {asMarkdown ? (
        <ReactMarkdown
          components={{
            a: MarkdownLink,
            table: Table,
            h1: ({ node, ...props }) => <Heading {...props} level={1} />,
            h2: ({ node, ...props }) => <Heading {...props} level={2} />,
            h3: ({ node, ...props }) => <Heading {...props} level={3} />,
            h4: ({ node, ...props }) => <Heading {...props} level={4} />,
            ...markdownComponents,
          }}
          remarkPlugins={[remarkGridTable, remarkSupersub]}
          rehypePlugins={[rehypeSlug]}
          remarkRehypeOptions={{
            handlers: { gridTable: mdast2hastGridTablesHandler() },
          }}
        >
          {skipTranslation ? children : t(children)}
        </ReactMarkdown>
      ) : skipTranslation ? (
        children
      ) : (
        <Trans t={t}>{children}</Trans>
      )}
    </Root>
  );
};

// @ts-expect-error - The Txt component requires a children prop, but it is technically possible to chose to render it as an HTML element which doesn't accept one, this cause a type warning here.
export default React.memo(React.forwardRef(Txt));
